import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { AppHeaderLink } from './app-header-link';
import { UpsellModalContentCodes } from '../../../constants/upsell-modal-content-codes';
import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';
import { useEnableAddOnCentral } from '../../../feature-flags/hooks/use-enable-add-on-central';
import { useEnableHomePage } from '../../../feature-flags/hooks/use-enable-home-page';
import { useEnableHomePage2 } from '../../../feature-flags/hooks/use-enable-home-page2';
import { useEnableLtlFreight } from '../../../feature-flags/hooks/use-enable-ltl-freight';
import { useEnableSuppliesNav } from '../../../feature-flags/hooks/use-enable-supplies-nav';
import { useAddOnsFeatureGate } from '../../../feature-gates/use-addons-feature-gate';
import { useAutomationRulesFeatureGate } from '../../../feature-gates/use-automation-rules-feature-gate';
import { useBarcodeScanningFeatureGate } from '../../../feature-gates/use-barcode-scanning-feature-gate';
import { useIsInsightsPageFeatureEnabled } from '../../../feature-gates/use-is-insights-page-feature-enabled';
import { useIsOrdersGridEnabled } from '../../../feature-gates/use-is-orders-grid-enabled';
import { useIsProductsPageFeatureEnabled } from '../../../feature-gates/use-is-products-page-feature-enabled';
import { useIsSellerOnFeatureGatedPlanProfile } from '../../../feature-gates/use-is-seller-on-feature-gated-plan-profile';
import { useIsShipStationAnalyticsFeatureEnabled } from '../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { useCountryConfig } from '../../../hooks/use-country-config';
import { useIsFulfillmentsTabVisible } from '../../../hooks/use-shipstation-fulfillment-whitelabel-enabled';
import { BASE_RETURNS_PATH } from '../../../routes/constants';
import { UserPermissionService, DEFAULT_ORDERS_PAGE_URL, DEFAULT_SHIPMENTS_PAGE_URL } from '../../../services/user-permission-service';
import { SellerProperties } from '../../../store/seller/seller-properties';
import { EventSourceStrings } from '../../../tracking/tracking-constants';
import {
  FEATURE_ROUTE_DASHBOARD,
  FEATURE_ROUTE_PRODUCTS,
  FEATURE_ROUTE_ONBOARD,
  FEATURE_ROUTE_CUSTOMERS,
  ADDON_MARKETPLACE,
  FEATURE_ROUTE_HOME,
  FEATURE_ROUTE_FULFILLMENTS,
  FEATURE_ROUTE_SCAN,
  FEATURE_ROUTE_AUTOMATIONS,
  FEATURE_ROUTE_SUPPLIES,
  FEATURE_ROUTE_FREIGHT,
} from '../constants/routes';
import { appHeaderMessages as messages } from '../messages';

export const checkIsActive =
  (expectedActiveRoute) =>
  (match, { pathname = '' }) => {
    const basePath = pathname.split('/')[1];
    return expectedActiveRoute === basePath;
  };

/**
 * @param {object} props
 * @param {boolean} [props.collapsed] - Whether the links are collapsed into a dropdown
 * @returns {React.JSX.Element}
 */
const AppHeaderLinks = ({ collapsed = false }) => {
  const isSellerOnFeatureGatedPlanProfile = useIsSellerOnFeatureGatedPlanProfile();

  const isSetupComplete = useSelector((state) => new SellerProperties({ state }).isSetupComplete);
  const enableHomePage = useEnableHomePage();
  const enableHomePage2 = useEnableHomePage2();
  const isHomePageEnabled = enableHomePage || enableHomePage2;
  const headerLink = isHomePageEnabled ? FEATURE_ROUTE_HOME : FEATURE_ROUTE_ONBOARD;
  const headerText = isHomePageEnabled ? messages.home : messages.onboard;
  const enhancedReturnsUpsellContentCode = isSellerOnFeatureGatedPlanProfile ? UpsellModalContentCodes.EnhancedReturns : undefined;

  const isShipStationAnalyticsEnabled = useIsShipStationAnalyticsFeatureEnabled();
  const { isUnlimitedAutomationRulesEnabled, isLimitedAutomationRulesEnabled } = useAutomationRulesFeatureGate();
  const isShipStationFulfillmentWhitelabelEnabled = useIsFulfillmentsTabVisible();
  const isAddOnCentralEnabled = useEnableAddOnCentral();
  const { isAddonMenuItemVisible } = useAddOnsFeatureGate();
  const isLtlFreightEnabled = useEnableLtlFreight();
  const isSuppliesNavEnabled = useEnableSuppliesNav();

  const { isFeatureEnabled: hasBarcodeScanningFeature } = useBarcodeScanningFeatureGate();
  const scanUpsellContentCode = isSellerOnFeatureGatedPlanProfile ? UpsellModalContentCodes.BarcodeScanning : undefined;

  const [showInsights] = useCountryConfig(CountryConfigNames.ShowInsights);
  const isInsightsPageFeatureEnabled = useIsInsightsPageFeatureEnabled();
  const insightsUpsellContentCode = isSellerOnFeatureGatedPlanProfile ? UpsellModalContentCodes.DashboardsViewOnly : undefined;

  const isOrdersGridEnabled = useIsOrdersGridEnabled();

  const isProductsPageFeatureEnabled = useIsProductsPageFeatureEnabled();
  const productsUpsellContentCode = isSellerOnFeatureGatedPlanProfile ? UpsellModalContentCodes.ProductCatalog : undefined;

  return (
    <>
      {(isHomePageEnabled || !isSetupComplete) && collapsed ? (
        <AppHeaderLink isCollapsed={collapsed} to={headerLink}>
          <FormattedMessage {...headerText} />
        </AppHeaderLink>
      ) : null}
      {showInsights ? (
        <AppHeaderLink
          isCollapsed={collapsed}
          isFeatureEnabled={isInsightsPageFeatureEnabled}
          requiredPermissionRole={UserPermissionService.reportingPermissionRole}
          source={EventSourceStrings.TOP_NAV_ANALYTICS}
          to={FEATURE_ROUTE_DASHBOARD}
          upsellContentCode={insightsUpsellContentCode}
        >
          <FormattedMessage {...(isShipStationAnalyticsEnabled ? messages.analytics : messages.dashboard)} />
        </AppHeaderLink>
      ) : null}
      <AppHeaderLink
        isCollapsed={collapsed}
        isFeatureEnabled={isUnlimitedAutomationRulesEnabled || isLimitedAutomationRulesEnabled}
        requiredPermissionRole={UserPermissionService.configurationPermissionRole}
        to={FEATURE_ROUTE_AUTOMATIONS}
      >
        <FormattedMessage {...messages.automations} />
      </AppHeaderLink>
      <AppHeaderLink
        isActive={checkIsActive('orders')}
        isCollapsed={collapsed}
        isFeatureEnabled={isOrdersGridEnabled}
        requiredPermissionRole={UserPermissionService.orderManagementPermissionRole}
        to={DEFAULT_ORDERS_PAGE_URL}
      >
        <FormattedMessage {...messages.orders} />
      </AppHeaderLink>
      <AppHeaderLink
        isActive={checkIsActive('shipments')}
        isCollapsed={collapsed}
        requiredPermissionRole={UserPermissionService.shippingPermissionRole}
        to={DEFAULT_SHIPMENTS_PAGE_URL}
      >
        <FormattedMessage {...messages.shipments} />
      </AppHeaderLink>
      <AppHeaderLink
        isActive={checkIsActive('returns')}
        isCollapsed={collapsed}
        requiredPermissionRole={UserPermissionService.shippingPermissionRole}
        source={EventSourceStrings.TOP_NAV_RETURNS}
        to={BASE_RETURNS_PATH}
        upsellContentCode={enhancedReturnsUpsellContentCode}
      >
        <FormattedMessage {...messages.returns} />
      </AppHeaderLink>
      <AppHeaderLink
        isCollapsed={collapsed}
        isFeatureEnabled={isProductsPageFeatureEnabled}
        requiredPermissionRole={UserPermissionService.productManagementPermissionRole}
        source={EventSourceStrings.TOP_NAV_PRODUCTS}
        to={FEATURE_ROUTE_PRODUCTS}
        upsellContentCode={productsUpsellContentCode}
      >
        <FormattedMessage {...messages.products} />
      </AppHeaderLink>
      <AppHeaderLink
        isCollapsed={collapsed}
        requiredPermissionRole={UserPermissionService.customerManagementPermissionRole}
        to={FEATURE_ROUTE_CUSTOMERS}
      >
        <FormattedMessage {...messages.customers} />
      </AppHeaderLink>
      <AppHeaderLink
        isCollapsed={collapsed}
        isFeatureEnabled={hasBarcodeScanningFeature}
        requiredPermissionRole={UserPermissionService.shippingPermissionRole}
        source={EventSourceStrings.TOP_NAV_SCAN}
        to={FEATURE_ROUTE_SCAN}
        upsellContentCode={scanUpsellContentCode}
      >
        <FormattedMessage {...messages.scan} />
      </AppHeaderLink>
      {isAddOnCentralEnabled && isAddonMenuItemVisible ? (
        <AppHeaderLink isCollapsed={collapsed} to={ADDON_MARKETPLACE}>
          <FormattedMessage {...messages.addOns} />
        </AppHeaderLink>
      ) : null}
      {isShipStationFulfillmentWhitelabelEnabled ? (
        <AppHeaderLink isCollapsed={collapsed} to={FEATURE_ROUTE_FULFILLMENTS}>
          <FormattedMessage {...messages.fulfillments} />
        </AppHeaderLink>
      ) : null}
      {isLtlFreightEnabled ? (
        <AppHeaderLink isActive={checkIsActive('freight')} isCollapsed={collapsed} to={FEATURE_ROUTE_FREIGHT}>
          <FormattedMessage {...messages.freight} />
        </AppHeaderLink>
      ) : null}
      {isSuppliesNavEnabled ? (
        <AppHeaderLink isActive={checkIsActive('supplies')} isCollapsed={collapsed} to={FEATURE_ROUTE_SUPPLIES}>
          <FormattedMessage {...messages.supplies} />
        </AppHeaderLink>
      ) : null}
    </>
  );
};

export default AppHeaderLinks;
